import React, {useEffect, useRef, useState} from 'react';
import '../../css/gardenPlanterStylings/PlotPlanterGardenStyling.css';
import PlotPlanterGardenVeggies from "./PlotPlanterGardenVeggies";
import LeafLoader from "../reusableComponents/LeafLoader";

const PlotPlanterGarden = ({ gardenMatrix, plants, autoPlantingActive, updateMap, algoPreviewChange, saveGardenClick}) => {
    const [normalizedMatrix, setNormalizedMatrix] = useState([]);
    const [plantMap, setPlantMap] = useState([]);
    const [draggedVeggie, setDraggedVeggie] = useState(null);
    const [plantCounts, setPlantCounts] = useState(null);
    const [plantColors, setPlantColors] = useState({}); // Track colors assigned to each plant type
    const [usedColorIndices, setUsedColorIndices] = useState(new Set()); // Track which colors are used
    const [hoveredPlant, setHoveredPlant] = useState(null);
    const tooltipRef = useRef(null);
    const [selectedVeggieColorChange, setselectedVeggieColorChange] = useState(null); // for changing plant space colors and whatnot
    const [showColorModal, setShowColorModal] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isWideScreen, setIsWideScreen] = useState(false);
    const [isTabletSized, setIsTabletSized] = useState(false);

    // NEW: Add state for drag preview
    const [dragPreview, setDragPreview] = useState({ row: -1, col: -1, valid: false });

    //checking if on mobile to resize cell sizes!!
    useEffect(() => {
        const updateBreakpoints = () => {
            const width = window.innerWidth;

            setIsMobile(width <= 768);
            setIsTabletSized(width > 768 && width <= 1038);  // Fixed: between mobile and desktop
            setIsWideScreen(width >= 1536);
        };

        updateBreakpoints();
        window.addEventListener('resize', updateBreakpoints);
        return () => window.removeEventListener('resize', updateBreakpoints);
    }, []);


    // loading state for saving garden
    const [isSavingGarden, setIsSavingGarden] = useState(false);

    const colorList = [
        "#9CCC65",
        "#FF7043",
        "#4FC3F7",
        "#FBC02D",
        "#BA68C8",
        "#F8BBD0",
        "#26A69A",
        "#EF5350",
        "#7986CB",
        "#81C784",
        "#FFA726",
        "#81D4FA",
        "#DCE775",
        "#CE93D8",
        "#A1887F",
        "#80CBC4",
        "#E57373",
        "#FFB74D",
        "#FFCA28",
        "#c78181",
        "#e7b275",
        "#4DB6AC",
        "#FF8A65",
        "#9575CD",
        "#9a7fa1",
        "#90CAF9"
    ];

    function normalizeArray(dataString) {
        let array = dataString.trim().split("\n").map(row => row.split(",").map(Number));
        array = array.filter(row => row.some(value => value !== 0));
        let transposed = array[0].map((_, colIndex) => array.map(row => row[colIndex]));
        transposed = transposed.filter(col => col.some(value => value !== 0));
        return transposed[0].map((_, rowIndex) => transposed.map(col => col[rowIndex]));
    }

    // Function to assign colors to plants synchronously
    const assignPlantColors = (algoMap) => {
        const newPlantColors = {};
        const newUsedIndices = new Set();

        // First pass: collect all unique plant names
        const plantNames = new Set();
        algoMap.forEach(row => {
            row.forEach(cell => {
                if (cell && cell.name) {
                    plantNames.add(cell.name);
                }
            });
        });

        // Assign colors to each plant type
        let colorIndex = 0;
        plantNames.forEach(plantName => {
            // Find the first unused color index
            while (newUsedIndices.has(colorIndex) && colorIndex < colorList.length) {
                colorIndex++;
            }

            // If we've used all colors, start cycling through them
            if (colorIndex >= colorList.length) {
                colorIndex = Math.floor(Math.random() * colorList.length);
            }

            newPlantColors[plantName] = colorList[colorIndex];
            newUsedIndices.add(colorIndex);
            colorIndex++;
        });

        // Update state
        setPlantColors(newPlantColors);
        setUsedColorIndices(newUsedIndices);

        // Return the map with colors assigned
        return algoMap.map(row => row.map(cell => {
            if (cell && cell.name) {
                return { ...cell, color: newPlantColors[cell.name] };
            }
            return cell;
        }));
    };

    // Function to get or assign a color to a plant type (for manual placement)
    const getPlantColor = (plantName) => {
        // If this plant already has a color, return it
        if (plantColors[plantName]) {
            return plantColors[plantName];
        }

        // Find the first unused color index
        let colorIndex = 0;
        while (usedColorIndices.has(colorIndex) && colorIndex < colorList.length) {
            colorIndex++;
        }

        // If we've used all colors, start cycling through them
        if (colorIndex >= colorList.length) {
            colorIndex = Math.floor(Math.random() * colorList.length);
        }

        const newColor = colorList[colorIndex];

        // Update the used colors and plant colors
        setUsedColorIndices(prev => new Set([...prev, colorIndex]));
        setPlantColors(prev => ({ ...prev, [plantName]: newColor }));

        return newColor;
    };

    // NEW: Function to check if a placement is valid
    const isValidPlacement = (rowIndex, colIndex, plantingWidth, plantingHeight) => {
        if (!draggedVeggie) return false;

        const rows = normalizedMatrix.length || 1;
        const cols = normalizedMatrix[0]?.length || 1;

        // Check bounds
        if (
            rowIndex + plantingHeight > rows ||
            colIndex + plantingWidth > cols ||
            rowIndex < 0 ||
            colIndex < 0
        ) return false;

        // Check for overlapping plants or invalid soil
        for (let r = 0; r < plantingHeight; r++) {
            for (let c = 0; c < plantingWidth; c++) {
                const targetRow = rowIndex + r;
                const targetCol = colIndex + c;
                if (
                    normalizedMatrix[targetRow][targetCol] !== 1 ||
                    plantMap[targetRow][targetCol]
                ) {
                    return false;
                }
            }
        }

        return true;
    };

    useEffect(() => {
        setPlantCounts(plants);
        const matrix = normalizeArray(gardenMatrix);
        setNormalizedMatrix(matrix);
        const emptyMap = matrix.map(row => row.map(() => null));

        if (algoPreviewChange && Array.isArray(algoPreviewChange)) {
            // console.log("Loading algorithm preview into grid:", algoPreviewChange);
            const coloredAlgoMap = assignPlantColors(algoPreviewChange);
            setPlantMap(coloredAlgoMap);
            updateMap(coloredAlgoMap);
        } else {
            setPlantMap(emptyMap);
            updateMap(emptyMap);
            // Reset color assignments when no algorithm preview
            setUsedColorIndices(new Set());
            setPlantColors({});
        }
    }, [gardenMatrix, plants, algoPreviewChange]); // Add algoPreviewChange to dependencies

    const sizes = {
        mobile: 200,
        tablet: 350,
        wide: 650,
        default: 500
    }
    const containerSize = sizes.mobile && isMobile ? sizes.mobile :
        sizes.tablet && isTabletSized ? sizes.tablet :
            sizes.wide && isWideScreen ? sizes.wide : sizes.default;
    const rows = normalizedMatrix.length || 1;
    const cols = normalizedMatrix[0]?.length || 1;
    const cellSize = Math.floor(containerSize / Math.max(rows, cols));

    const handleDrop = (e, rowIndex, colIndex) => {
        e.preventDefault();
        if (!draggedVeggie) return;

        const { plantingWidth = 1, plantingHeight = 1 } = draggedVeggie;

        // Check if we have any of this veggie available
        const currentVeggie = plantCounts.find(plant => plant.name === draggedVeggie.name);
        if (!currentVeggie || currentVeggie.count <= 0) {
            // Clear preview after invalid drop
            setDragPreview({ row: -1, col: -1, valid: false });
            return;
        }

        // Check bounds and occupancy
        if (
            rowIndex + plantingHeight > rows ||
            colIndex + plantingWidth > cols
        ) {
            // Clear preview after invalid drop with a short delay for visual feedback
            setTimeout(() => {
                setDragPreview({ row: -1, col: -1, valid: false });
            }, 300); // 300ms delay
            return;
        }

        // Check for overlapping plants or invalid soil (value !== 1)
        for (let r = 0; r < plantingHeight; r++) {
            for (let c = 0; c < plantingWidth; c++) {
                if (
                    normalizedMatrix[rowIndex + r][colIndex + c] !== 1 ||
                    plantMap[rowIndex + r][colIndex + c]
                ) {
                    // Clear preview after invalid drop with a short delay for visual feedback
                    setTimeout(() => {
                        setDragPreview({ row: -1, col: -1, valid: false });
                    }, 300); // 300ms delay
                    return;
                }
            }
        }

        // ... rest of successful placement logic remains the same
        const plantColor = getPlantColor(draggedVeggie.name);

        const newMap = plantMap.map(row => [...row]);
        for (let r = 0; r < plantingHeight; r++) {
            for (let c = 0; c < plantingWidth; c++) {
                newMap[rowIndex + r][colIndex + c] = {
                    ...draggedVeggie,
                    rootCell: r === 0 && c === 0,
                    color: plantColor
                };
            }
        }

        const updatedPlantCounts = plantCounts.map(plant =>
            plant.name === draggedVeggie.name
                ? { ...plant, count: plant.count - 1 }
                : plant
        );

        setPlantMap(newMap);
        updateMap(newMap);
        setPlantCounts(updatedPlantCounts);

        // Clear drag preview immediately on successful drop
        setDragPreview({ row: -1, col: -1, valid: false });
    };


    const handleVeggieDragStart = (e, veggie) => {
        setDraggedVeggie(veggie);
    };

    // NEW: Handle drag over for preview
    const handleDragOver = (e, rowIndex, colIndex) => {
        if (!draggedVeggie) return;

        e.preventDefault();

        const { plantingWidth = 1, plantingHeight = 1 } = draggedVeggie;
        const valid = isValidPlacement(rowIndex, colIndex, plantingWidth, plantingHeight);

        setDragPreview({
            row: rowIndex,
            col: colIndex,
            valid: valid
        });
    };

    // NEW: Handle drag leave to clear preview
    const handleDragLeave = (e) => {
        // Only clear if we're leaving the entire grid area
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragPreview({ row: -1, col: -1, valid: false });
        }
    };

    // Function to get the background color for a cell
    const getCellBackgroundColor = (rowIndex, colIndex, value) => {
        if (value !== 1) return "transparent";

        // NEW: Check for drag preview overlay
        if (draggedVeggie && dragPreview.row !== -1 && dragPreview.col !== -1) {
            const { plantingWidth = 1, plantingHeight = 1 } = draggedVeggie;

            // Check if current cell is within the preview area
            if (
                rowIndex >= dragPreview.row &&
                rowIndex < dragPreview.row + plantingHeight &&
                colIndex >= dragPreview.col &&
                colIndex < dragPreview.col + plantingWidth
            ) {
                // Return different colors based on whether placement is valid
                return dragPreview.valid
                    ? "rgba(76, 175, 80, 1)" // Green overlay for valid placement
                    : "rgba(244, 67, 54, 1)"; // Red overlay for invalid placement
            }
        }

        const plant = plantMap[rowIndex]?.[colIndex];
        if (plant) {
            const baseColor = plant.color;

            if (
                hoveredPlant &&
                plantMap[hoveredPlant.rootRow]?.[hoveredPlant.rootCol]?.name === plant.name
            ) {
                const { plantingHeight = 1, plantingWidth = 1 } =
                    plantMap[hoveredPlant.rootRow][hoveredPlant.rootCol];

                // Check if current cell is inside the hovered instance's bounding box
                if (
                    rowIndex >= hoveredPlant.rootRow &&
                    rowIndex < hoveredPlant.rootRow + plantingHeight &&
                    colIndex >= hoveredPlant.rootCol &&
                    colIndex < hoveredPlant.rootCol + plantingWidth
                ) {
                    // return lightenHex(baseColor, 30); // highlight hovered instance
                    return baseColor;
                }
            }

            return `${baseColor}99`; // normal
        }

        return "#6d4c41"; // soil
    };


    function lightenHex(hex, percent) {
        // Remove #
        hex = hex.replace(/^#/, "");

        // Expand short form (#abc)
        if (hex.length === 3) {
            hex = hex.split("").map(c => c + c).join("");
        }

        const num = parseInt(hex, 16);
        let r = (num >> 16) & 0xff;
        let g = (num >> 8) & 0xff;
        let b = num & 0xff;

        // Lighten each channel toward 255
        r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
        g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
        b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

        return `#${((1 << 24) + (r << 16) + (g << 8) + b)
            .toString(16)
            .slice(1)}`;
    }

    const handleRightClick = (e, rowIndex, colIndex) => {
        e.preventDefault();

        setHoveredPlant(null);

        const plant = plantMap[rowIndex]?.[colIndex];
        if (!plant) return;

        let rootRow = rowIndex;
        let rootCol = colIndex;

        // If this cell isn't the root, find the root cell
        if (!plant.rootCell) {
            const { plantingHeight = 1, plantingWidth = 1, name } = plant;
            // Search backwards from the clicked cell within the bounds of the planting dimensions
            for (let r = rowIndex; r >= rowIndex - plantingHeight + 1; r--) {
                for (let c = colIndex; c >= colIndex - plantingWidth + 1; c--) {
                    if (
                        plantMap[r]?.[c] &&
                        plantMap[r][c].name === name &&
                        plantMap[r][c].rootCell
                    ) {
                        rootRow = r;
                        rootCol = c;
                        break;
                    }
                }
            }
        }

        const rootPlant = plantMap[rootRow]?.[rootCol];
        if (!rootPlant) return;

        const { plantingHeight = 1, plantingWidth = 1, name } = rootPlant;

        const newMap = plantMap.map(row => [...row]);

        for (let r = 0; r < plantingHeight; r++) {
            for (let c = 0; c < plantingWidth; c++) {
                const targetRow = rootRow + r;
                const targetCol = rootCol + c;
                if (newMap[targetRow]?.[targetCol]?.name === name) {
                    newMap[targetRow][targetCol] = null;
                }
            }
        }

        const updatedPlantCounts = plantCounts.map(plant =>
            plant.name === name ? { ...plant, count: plant.count + 1 } : plant
        );

        setPlantMap(newMap);
        updateMap(newMap);
        setPlantCounts(updatedPlantCounts);
    };

    const handlePlantedVeggieClick = (plantName) => {
        setselectedVeggieColorChange(plantName);
        setShowColorModal(true);
    };

    const changeVeggieColor = (plantName, newColor) => {
        // Update plant colors mapping
        setPlantColors(prev => ({
            ...prev,
            [plantName]: newColor
        }));

        // Update used colors (optional, for managing available pool)
        const newIndex = colorList.indexOf(newColor);
        setUsedColorIndices(prev => {
            const newSet = new Set(prev);
            newSet.add(newIndex);
            return newSet;
        });

        // Update plantMap with new color
        const updatedMap = plantMap.map(row =>
            row.map(cell =>
                cell && cell.name === plantName ? { ...cell, color: newColor } : cell
            )
        );

        setPlantMap(updatedMap);
        updateMap(updatedMap);
        setShowColorModal(false);
    };


    //PERFECTLY WORKING JUST SAVES TOO QUICK

    const handleGardenSaveClick = async () => {
        setIsSavingGarden(true);

        try {
            // Call the save function and wait for it to complete
            await saveGardenClick(plantMap);
        } catch (error) {
            console.error("Error saving garden:", error);
        } finally {
            // Always reset loading state
            setIsSavingGarden(false);
        }
    }

    return (
        <div className={`plot-planter-garden-container`} style={{ position: 'relative' }}>
            <div className={`plot-planter-garden-container-split`} style={{}}>
                {/* Draggable veggie selectors - show at top when autoPlantingActive is false */}
                {/*{!autoPlantingActive && (*/}
                {/*    <div className={`plot-planter-garden-veggies`}>*/}
                {/*        <PlotPlanterGardenVeggies selectedVeggies={plants} veggieCounts={plantCounts} onVeggieDragStart={handleVeggieDragStart}/>*/}
                {/*    </div>*/}
                {/*)}*/}
                <div className={`plot-planter-garden-veggies`}>
                    <PlotPlanterGardenVeggies selectedVeggies={plants} veggieCounts={plantCounts}
                                              onVeggieDragStart={handleVeggieDragStart}/>
                </div>

                {hoveredPlant && (
                    <div
                        ref={tooltipRef}
                        className="custom-tooltip"
                        style={{
                            position: "fixed",
                            top: hoveredPlant.top + (hoveredPlant.height / 2),
                            left: hoveredPlant.right + 10,
                            margin: "0px auto",
                            transform: "translateY(-50%)",
                            background: "#333",
                            color: "#fff",
                            padding: "0.5em",
                            borderRadius: "4px",
                            fontSize: "0.85em",
                            whiteSpace: "nowrap",
                            pointerEvents: "none",
                            zIndex: 999,
                            opacity: 1,
                            transition: "opacity 0.2s ease-in-out",
                        }}
                    >
                        {hoveredPlant.name}
                    </div>
                )}

                <div className={`plot-planter-garden-grid-container`}>
                    {/* Garden grid */}
                    <div
                        className="plot-planter-garden-image-matrix"
                        onDragLeave={handleDragLeave} // NEW: Add drag leave handler
                        style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
                            gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
                            gap: "0px",
                            width: `${cellSize * cols}px`,
                            height: `${cellSize * rows}px`,
                            border: "5px solid #065e45",
                            borderRadius: "10px",
                            padding: "3em"
                        }}
                    >
                        {normalizedMatrix.map((row, rowIndex) =>
                            row.map((value, colIndex) => (
                                <div
                                    key={`${rowIndex}-${colIndex}`}
                                    onDragOver={(e) => {
                                        if (value === 1) {
                                            e.preventDefault();
                                            handleDragOver(e, rowIndex, colIndex); // NEW: Add drag over handler
                                        }
                                    }}
                                    onDrop={(e) => {
                                        if (value === 1) handleDrop(e, rowIndex, colIndex);
                                    }}
                                    onContextMenu={(e) => handleRightClick(e, rowIndex, colIndex)}
                                    className={`garden-cell ${value === 1 ? "has-background" : ""}`}
                                    style={{
                                        position: "relative",
                                        width: `${cellSize}px`,
                                        height: `${cellSize}px`,
                                        backgroundColor: getCellBackgroundColor(rowIndex, colIndex, value),
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: value === 1 ? "1px solid #5c4037" : "",
                                        fontSize: "24px",
                                        userSelect: "none",
                                        overflow: "visible"
                                    }}
                                >
                                    {plantMap[rowIndex]?.[colIndex]?.rootCell && (
                                        <div
                                            style={{
                                                // Use grid positioning to span multiple cells
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                width: `${cellSize * (Number(plantMap[rowIndex][colIndex].plantingWidth) || 1)}px`,
                                                height: `${cellSize * (Number(plantMap[rowIndex][colIndex].plantingHeight) || 1)}px`,
                                                zIndex: 1,
                                                pointerEvents: "auto", // Allow drop events to pass through (WAS NONE NOW AUTO TO ALLOW HOVER TOOLTIP!!)
                                            }}
                                        >
                                            <img
                                                src={plants.find(p => p.name === plantMap[rowIndex][colIndex].name)?.veggieImage || plantMap[rowIndex][colIndex].image}
                                                alt={plantMap[rowIndex][colIndex].name}
                                                onClick={() => handlePlantedVeggieClick(plantMap[rowIndex][colIndex].name)}
                                                onContextMenu={(e) => handleRightClick(e, rowIndex, colIndex)}

                                                onMouseEnter={(e) => {
                                                    const {
                                                        name,
                                                        plantingHeight = 1,
                                                        plantingWidth = 1
                                                    } = plantMap[rowIndex][colIndex];

                                                    // Find root (top-left) of this plant instance
                                                    let rootRow = rowIndex;
                                                    let rootCol = colIndex;
                                                    if (!plantMap[rowIndex][colIndex].rootCell) {
                                                        for (let r = rowIndex; r >= rowIndex - plantingHeight + 1; r--) {
                                                            for (let c = colIndex; c >= colIndex - plantingWidth + 1; c--) {
                                                                if (plantMap[r]?.[c]?.name === name && plantMap[r][c].rootCell) {
                                                                    rootRow = r;
                                                                    rootCol = c;
                                                                    break;
                                                                }
                                                            }
                                                        }
                                                    }

                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setHoveredPlant({
                                                        name,
                                                        rootRow,
                                                        rootCol,
                                                        top: rect.top,
                                                        left: rect.left,
                                                        right: rect.right,
                                                        width: rect.width,
                                                        height: rect.height,
                                                    });
                                                }}

                                                onMouseLeave={() => setHoveredPlant(null)}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "contain"
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))
                        )}

                        <button
                            onClick={handleGardenSaveClick}
                            disabled={isSavingGarden}
                            style={{
                                position: "absolute",
                                bottom: "-5px", // Align to the border edge
                                right: "-5px", // Align to the border edge
                                padding: "0.6em 1.2em",
                                backgroundColor: "#065e45",
                                color: "#fff",
                                border: "none",
                                borderTopLeftRadius: "5px",
                                borderBottomRightRadius: "10px", // Match the grid's border radius
                                cursor: isSavingGarden ? "not-allowed" : "pointer",
                                fontSize: "0.9em",
                                fontWeight: "bold",
                                opacity: isSavingGarden ? 0.7 : 1,
                                zIndex: 10 // Ensure it appears above grid content
                            }}
                        >
                            {isSavingGarden ? "Saving..." : "Save Layout"}
                        </button>
                    </div>
                </div>

                {showColorModal && selectedVeggieColorChange && (
                    <div className="veggie-color-change-modal-overlay">
                        <div className={`veggie-color-change-modal-content`}>
                            <div className={`veggie-color-change-modal-title`}>Select Plot Color
                                for {selectedVeggieColorChange}</div>
                            <div className={`veggie-color-change-modal`}>
                                {colorList.map((color, index) => (
                                    <div
                                        key={index}
                                        onClick={() => changeVeggieColor(selectedVeggieColorChange, color)}
                                        style={{
                                            width: '30px',
                                            height: '30px',
                                            backgroundColor: color,
                                            borderRadius: '50%',
                                            cursor: 'pointer',
                                            border: plantColors[selectedVeggieColorChange] === color ? '2px solid #000' : '1px solid #ccc'
                                        }}
                                        title={color}
                                    />
                                ))}
                            </div>
                            <button className={`veggie-color-change-button`}
                                    onClick={() => setShowColorModal(false)}>Close
                            </button>
                        </div>
                    </div>
                )}

                {/* Draggable veggie selectors - show at bottom when autoPlantingActive is true */}
                {/*{autoPlantingActive && (*/}
                {/*    <div className={`plot-planter-garden-veggies`}>*/}
                {/*        <PlotPlanterGardenVeggies selectedVeggies={plants} veggieCounts={plantCounts} onVeggieDragStart={handleVeggieDragStart}/>*/}
                {/*    </div>*/}
                {/*)}*/}
            </div>

            {/* Loading overlay - only visible when saving */}
            {isSavingGarden && (
                <div
                    className="plot-planter-loading-overlay"
                    style={{
                        position: 'absolute',
                        top: '25%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: '2rem',
                        borderRadius: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        minWidth: '200px',
                        minHeight: '150px'
                    }}
                >
                    <LeafLoader
                        count={7}
                        size={20}
                        speed="fast"
                        radius={30}
                        top={'35%'}
                    />
                    <p style={{
                        marginTop: '20px',
                        color: '#f3f3f3',
                        fontSize: '16px',
                        fontWeight: '500',
                        textAlign: 'center'
                    }}>
                        Saving your garden...
                    </p>
                </div>
            )}
        </div>
    );
};

export default PlotPlanterGarden;